// src/components/ui/AIAssistant.js - VERSIÓN FINAL-FINAL ROBUSTA
import React, { useState, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import ReactMarkdown from 'react-markdown'; 

const RobotIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 7h14m-14 4h14m-18 0a4 4 0 118 0 4 4 0 01-8 0zm2-2h4m-4 8h4m-4 4h4M3 13h18a2 2 0 012 2v2a2 2 0 01-2 2H3a2 2 0 01-2-2v-2a2 2 0 012-2z" />
    </svg>
);

function AIAssistant({ showModal, currentUser }) {
    const [isOpen, setIsOpen] = useState(false);
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([
        { text: '¡Hola! Soy tu asistente de datos. ¿En qué puedo ayudarte hoy?', sender: 'ai' }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const functions = getFunctions(getApp());
    const askAIV2 = httpsCallable(functions, 'askAIV2');
    const hasAIPermission = currentUser?.permissions?.GENERAL?.ask_ai_permission;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if(isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    const handleAsk = async (e) => {
        e.preventDefault();
        const userQuestion = question.trim();
        if (!userQuestion || isLoading) return; // Añadimos isLoading aquí también

        setIsLoading(true);
        const currentHistory = [...messages, { text: userQuestion, sender: 'user' }];
        setMessages(currentHistory);
        setQuestion('');
        
        // Esta línea es opcional, pero ayuda a que el "Pensando..." aparezca más rápido
        setTimeout(() => {
            setMessages(prev => [...prev, { text: 'Pensando...', sender: 'ai', isLoading: true }]);
        }, 100);

        try {
            const result = await askAIV2({ 
                question: userQuestion, 
                // Enviamos el historial ANTES de añadir la pregunta del usuario actual
                history: messages 
            });

            // ---- CAMBIO CLAVE: LECTURA SEGURA DE LA RESPUESTA ----
            // Usamos "?." para comprobar que cada parte del objeto existe antes de acceder a él.
            // Si algo no existe, en lugar de romperse, usará el texto alternativo.
            const aiAnswer = result?.data?.answer || "Lo siento, ocurrió un error al procesar la respuesta.";

            setMessages(prev => [
                ...prev.filter(m => !m.isLoading), // Quita el mensaje "Pensando..."
                { text: aiAnswer, sender: 'ai' }     // Añade la respuesta segura
            ]);

        } catch (error) {
            console.error("Error al llamar a la IA:", error);
            const errorMessage = "Lo siento, hubo un problema. Por favor, intenta de nuevo.";
            
            setMessages(prev => [
                ...prev.filter(m => !m.isLoading), // Quita el mensaje "Pensando..."
                { text: errorMessage, sender: 'ai' }
            ]);

            // Tu modal de error que ya funciona bien
            showModal("Error de IA", `Hubo un problema al contactar a la IA. Código: ${error.code || 'Desconocido'}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (!hasAIPermission) return null;

    return (
        <div className={`fixed bottom-4 right-4 z-40 transition-all duration-300 ${isOpen ? 'w-96 h-[500px]' : 'w-14 h-14'}`}>
            <button onClick={() => setIsOpen(!isOpen)} className={`absolute bottom-0 right-0 p-3 rounded-full shadow-lg bg-sky-600 text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-opacity-75 ${isOpen ? 'z-50' : ''}`} title={isOpen ? 'Cerrar Asistente de IA' : 'Abrir Asistente de IA'}>
                <RobotIcon />
            </button>

            {isOpen && (
                <div className="absolute top-0 left-0 w-full h-full bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col overflow-hidden">
                    <div className="bg-sky-600 text-white p-3 flex justify-between items-center text-lg font-semibold">
                        <span>Pregúntale a la IA</span>
                        <button onClick={() => setIsOpen(false)} className="text-white hover:text-gray-200 text-xl font-bold">&times;</button>
                    </div>
                    <div className="flex-grow p-4 overflow-y-auto text-sm bg-gray-50 flex flex-col space-y-3">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`p-2 px-3 rounded-lg max-w-[85%] ${msg.sender === 'user' ? 'bg-gray-200 text-gray-800' : 'bg-blue-100 text-blue-800'} ${msg.isLoading ? 'animate-pulse' : ''}`}>
                                    <div className="prose prose-sm max-w-none">
                                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                    <form onSubmit={handleAsk} className="p-3 border-t border-gray-200 bg-white">
                        <textarea className="w-full p-2 border border-gray-300 rounded-md focus:ring-sky-500 focus:border-sky-500 resize-none mb-2 text-sm" rows="2" placeholder="Escribe tu pregunta aquí..." value={question} onChange={(e) => setQuestion(e.target.value)} disabled={isLoading}></textarea>
                        <button type="submit" className="w-full btn-primary py-2 text-sm" disabled={isLoading}>
                            {isLoading ? 'Enviando...' : 'Preguntar'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
}

export default AIAssistant;